'use client';

import { useState } from 'react';

import { ExpandableHtml } from '@ps/ui/molecules/expandable-html';
import { ChevronDown, ChevronUp } from 'lucide-react';

type ProductDescriptionProps = {
  /** Pre-cleaned + sanitized description HTML (the parent sanitizes server-side). */
  html: string;
  /** Heading + chevron that collapses the whole block. Non-digital default. */
  isCollapsible?: boolean;
};

const bodyClassName =
  'text-label leading-[18px] font-medium text-black [&_img]:w-full [&_p]:my-0';

/**
 * Listing description. Mirrors RN `ListingDescriptionRow` + `ListingDescription`:
 * a collapsible "Description" heading (non-digital/meeting only) wrapping the
 * body. The body's truncate / "Show More" toggle reuses the shared
 * `ExpandableHtml` molecule rather than re-implementing it.
 */
export function ProductDescription({
  html,
  isCollapsible = true,
}: ProductDescriptionProps) {
  const [open, setOpen] = useState(true);

  if (!html) return null;

  return (
    <section className="px-4">
      {isCollapsible ? (
        <button
          type="button"
          onClick={() => setOpen(prev => !prev)}
          className="mb-2 flex w-full items-center justify-between text-left"
        >
          <span className="text-label font-bold uppercase tracking-wide text-black">
            Description
          </span>
          {open ? (
            <ChevronUp className="size-4 text-zinc-500" />
          ) : (
            <ChevronDown className="size-4 text-zinc-500" />
          )}
        </button>
      ) : null}

      {open ? (
        isCollapsible ? (
          <ExpandableHtml
            html={html}
            collapsedClassName="line-clamp-3"
            expandLabel="Show More"
            collapseLabel="Show Less"
            className={`listing-description-text max-w-none break-words ${bodyClassName}`}
            buttonClassName="text-product-detail-accent decoration-product-detail-accent"
          />
        ) : (
          <div
            className={`listing-description-text max-w-none break-words ${bodyClassName}`}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )
      ) : null}
    </section>
  );
}
